/**
 * PHASE D — "Managed by Platform" banner.
 *
 * Shown when a Platform Staff member has an open `admin_takeover_sessions`
 * row for the current tenant. Defaults to **silent mode** (no banner) so
 * support sessions can run quietly; explicit non-silent sessions render a
 * persistent top bar with store + manager context.
 */
import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useTenantStore } from "@/lib/tenantContext";

interface ActiveTakeover {
  id: string;
  admin_user_id: string;
  admin_username: string | null;
  started_at: string;
  support_intent: string | null;
  silent: boolean | null;
  reason: string | null;
  store_owner_id: string;
  store_title: string;
  store_slug: string;
}

export default function ManagedByPlatformBanner() {
  const tenantId = useTenantStore((s) => s.tenantId);
  const [session, setSession] = useState<ActiveTakeover | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setSession(null);
      return;
    }
    let alive = true;
    const fetchSession = async () => {
      const { data } = await supabase.rpc("active_takeover_for", { _tenant: tenantId });
      if (!alive) return;
      const row = Array.isArray(data) && data.length > 0 ? (data[0] as ActiveTakeover) : null;
      setSession(row);
    };
    fetchSession();
    const interval = setInterval(fetchSession, 30_000);
    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, [tenantId]);

  if (!session || session.silent) return null;

  const started = new Date(session.started_at).toLocaleString();

  return (
    <div className="w-full bg-amber-500/15 border-b border-amber-500/40 text-amber-900 dark:text-amber-100 px-4 py-2 text-xs sm:text-sm flex flex-wrap items-center gap-x-4 gap-y-1 z-50">
      <span className="flex items-center gap-2 font-semibold">
        <ShieldCheck className="w-4 h-4" />
        Managed by Platform
      </span>
      <span>
        Store: <strong>{session.store_title}</strong> · {session.store_slug}
      </span>
      <span>
        Current Manager: <strong>{session.admin_username ?? session.admin_user_id.slice(0, 8)}</strong>
      </span>
      <span>Since: {started}</span>
      {session.support_intent && (
        <span>
          Intent: <strong>{session.support_intent}</strong>
        </span>
      )}
    </div>
  );
}
